import React, {
    type AnchorHTMLAttributes,
    type MouseEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { ReactComponent as ClockIcon } from '@pkg/assets/icons/v2/clock.svg';

import Tag from '../Tag';

import './Featured.scss';

interface ContainerStyles {
    background?: string;
    backgroundImage?: string;
}

export interface FeaturedCardProps extends ContainerStyles {
    countdown?: string;
    headline?: string;
    subheadline?: string;
    headlineTag?: string;
    videoUrl?: string;
    tag?: string;
    icon?: 'star';
    link?: string;
    linkOpenSameTab?: boolean;
    size?: 'small' | 'medium' | 'large' | 'xlarge';
    theme?: 'light' | 'dark';
    onClick?: (event: MouseEvent<HTMLElement>) => void;
}

export function FeaturedCard({
    countdown,
    headline,
    subheadline,
    headlineTag,
    videoUrl,
    tag,
    icon,
    link,
    linkOpenSameTab,
    background,
    backgroundImage,
    size = 'medium',
    theme = 'dark',
    onClick,
}: FeaturedCardProps) {
    const [canPlay, setCanPlay] = useState(false);

    const videoBackgroundRef = useRef<HTMLVideoElement>(null);

    let headerElement: ReactNode;
    let countdownElement: ReactNode;
    let subheadlineElement: ReactNode;
    let overlayElement: ReactNode;

    if (tag) {
        headerElement = (
            <div className="yat-card-header">
                <Tag label={tag} />
            </div>
        );
    } else if (icon) {
        headerElement = (
            <div className="yat-card-header">
                <div className="yat-card-icon">
                    <div className={`yat-card-icon-${icon}`} />
                </div>
            </div>
        );
    }

    const containerStyle: ContainerStyles = {};
    const className = ['yat-card'];

    // Set background
    if (backgroundImage) {
        containerStyle.backgroundImage = `url(${backgroundImage})`;
        className.push('yat-card-image-background');
    } else if (background) {
        containerStyle.background = background;
    }

    // Set countdown element
    if (countdown) {
        countdownElement = (
            <div className="yat-card-countdown">
                <ClockIcon aria-hidden="true" />
                <div>{countdown}</div>
            </div>
        );
    }

    // Set subheadline element (xlarge card)
    if (size === 'xlarge' && subheadline) {
        subheadlineElement = <h6 className="yat-card-subheadline">{subheadline}</h6>;
    }

    // Set the overlay at the bottom
    if (backgroundImage) {
        overlayElement = <div className="yat-card-overlay" />;
    }

    const hrefProps: AnchorHTMLAttributes<HTMLAnchorElement> = link
        ? {
              href: link,
              target: linkOpenSameTab ? undefined : '_blank',
          }
        : {};

    // TODO: Consider hook or helper for this.
    const handlePlay = useCallback(() => {
        const video = videoBackgroundRef.current;

        if (video && canPlay) {
            const playPromise = video.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        return video.play();
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                        return video.pause();
                    });
            }
        }
    }, [canPlay]);

    useLayoutEffect(() => {
        const video = videoBackgroundRef?.current;

        if (video) {
            const isPlaying = Boolean(
                video &&
                    video.currentTime > 0 &&
                    !video.paused &&
                    !video.ended &&
                    video.readyState > video.HAVE_CURRENT_DATA,
            );

            setCanPlay(!isPlaying);
        }
    }, []);

    const videoBackground = useMemo(() => {
        if (videoUrl) {
            return (
                <video
                    onCanPlay={() => setCanPlay(true)}
                    className="yat-card-video-bg"
                    muted
                    playsInline
                    ref={videoBackgroundRef}
                    webkit-playsinline="true"
                >
                    <source src={videoUrl} type="video/mp4" />
                </video>
            );
        }
    }, [videoUrl, videoBackgroundRef]);

    const videoPlayControl = useMemo(() => {
        if (videoUrl) {
            return {
                onMouseEnter: () => handlePlay(),
                onTouchStart: () => handlePlay(),
                onMouseLeave: () => videoBackgroundRef.current?.pause(),
            };
        }
    }, [handlePlay, videoBackgroundRef, videoUrl]);

    useEffect(() => {
        videoBackgroundRef?.current?.load();
    }, [videoUrl]);

    let headlineTagElement: ReactNode;

    if (headlineTag) {
        headlineTagElement = (
            <div className="yat-card-headline-tag">
                <Tag label={headlineTag} />
            </div>
        );
    }

    const onClickProps = typeof onClick !== 'undefined' ? { onClick } : {};

    return (
        <a
            {...hrefProps}
            {...onClickProps}
            rel="noopener noreferrer"
            className={`yat-card-${size} yat-card-theme-${theme}`}
            {...videoPlayControl}
        >
            <div className={className.join(' ')} style={containerStyle}>
                {videoBackground}

                <div className="yat-card-canvas">
                    {headerElement}

                    <div className="yat-card-content">
                        <div>{/* Content goes here */}</div>
                    </div>

                    {!headline || (
                        <div className="yat-card-headline-container" style={{}}>
                            {headlineTagElement}
                            {countdownElement}
                            <h5 className="yat-card-headline">{headline}</h5>
                            {subheadlineElement}
                        </div>
                    )}
                </div>
                {overlayElement}
            </div>
        </a>
    );
}
